import React, { useState, useEffect, useRef } from "react";
import ExcelJS from 'exceljs'; // Import ExcelJS
import { saveAs } from "file-saver";

// Import your existing GET API scripts
import getWorkTypes from "./api_scripts/get/getWorkTypes";
import getTeams from "./api_scripts/get/getTeam";
import getSkillProfiles from "./api_scripts/get/getSkillProfile";
import getSkillDefinitions from "./api_scripts/get/getSkillDefinitions";
import getQueues from "./api_scripts/get/getQueues"; // Assuming you have this API
import getUsersList from "./api_scripts/get/getUsersList";
import getDesktopProfiles from "./api_scripts/get/getDesktopProfile"; // Assuming you have this API
import getBusinessHours from "./api_scripts/get/getBusinessHours"; // Assuming you have this API
import getHolidayList from "./api_scripts/get/getHolidayList"; // Assuming you have this API
import getOverrides from "./api_scripts/get/getOverrideList"; // Assuming you have this API
import getAddressBook from "./api_scripts/get/getAddressBook"; // Assuming you have this API
import getAddressBookEntries from "./api_scripts/get/getAddressBookEntries"; // Assuming you have this API
import getEntryPoints from "./api_scripts/get/getEntryPoints"; // Assuming you have this API
import getDialedNumberMappings from "./api_scripts/get/getDialedNumberMappings";
import getAuxCodes from "./api_scripts/get/getAuxCodes"; // Assuming you have getAuxCode.js
import getUserProfiles from "./api_scripts/get/getUserProfile"; // Assuming you have this API
import getMultimediaProfiles from "./api_scripts/get/getMultimediaList"; // Assuming you have this API
import getGlobalVariables from "./api_scripts/get/getCADVars"; // Assuming you have this API
import getSites from "./api_scripts/get/getSite"; // Assuming you have this API
import getOutdialAnis from "./api_scripts/get/getOutdialAnis";
import getOutdialAniEntries from "./api_scripts/get/getOutdialAniEntries";


const AsBuiltBuilder = ({ onBackToOptionSelector, authData }) => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState([]);
  const [fetchedData, setFetchedData] = useState({}); // Stores fetched data by type
  const [collectedData, setCollectedData] = useState({}); // Enhanced collection tracking
  const [currentStep, setCurrentStep] = useState(null); // Track current processing step
  const [completedSteps, setCompletedSteps] = useState([]); // Track completed steps
  const [totalSteps, setTotalSteps] = useState(0); // Total steps for progress calculation
  const [canDownload, setCanDownload] = useState(false); // Control download button state
  // Gemini functionality commented out as requested - removed "Description (Gemini)" columns and AI analysis
  const logContainerRef = useRef(null);
  const [exportProgress, setExportProgress] = useState(0); // For export progress bar

  // Define all possible sheets and their default selection state
  const initialSheetSelection = [
    { name: "Revisions", selected: true, dataKey: null, headerStartRow: 0 },
    { name: "Introduction", selected: true, dataKey: null, headerStartRow: 0 },
    { name: "Summary", selected: true, dataKey: "summaryData", headerStartRow: 0, headers: ["Category", "Count", "Notes"] }, // New Summary Sheet - moved here
    { name: "Unreferenced Objects", selected: true, dataKey: "unreferencedObjects", headerStartRow: 0, headers: ["Object Type", "Object Name", "Object ID", "Notes"] }, // New Unreferenced Objects Sheet
    { name: "Acceptance", selected: true, dataKey: null, headerStartRow: 0 },
    { name: "Register", selected: true, dataKey: null, headerStartRow: 0 },
    { name: "Status", selected: true, dataKey: null, headerStartRow: 0 },
    { name: "Pre-Discovery Questionnaire", selected: true, dataKey: null, headerStartRow: 0 },
    { name: "Naming Scheme", selected: true, dataKey: null, headerStartRow: 2 },
    { name: "Contact Center Timers", selected: true, dataKey: null, headerStartRow: 2 },
    { name: "Service Level Threshold", selected: true, dataKey: null, headerStartRow: 2 },
    { name: "Hours of Operation", selected: true, dataKey: "businessHours", headerStartRow: 1 },
    { name: "Holidays", selected: true, dataKey: "holidayLists", headerStartRow: 1 },
    { name: "Overrides", selected: true, dataKey: "overrides", headerStartRow: 1 },
    { name: "Sites", selected: true, dataKey: "sites", headerStartRow: 1 },
    { name: "Dialed Number Mappings", selected: true, dataKey: "dialedNumberMappings", headerStartRow: 1, headers: ["Object ID", "Number (DID/Toll Free)", "LOB / Call Flow", "Description", "Max Active Calls", "Entry Point", "Notes"/* , "Description (Gemini)" */] },
    { name: "Entry Points", selected: true, dataKey: "entryPoints", headerStartRow: 1 },
    { name: "Entry Point Formulas", selected: true, dataKey: null, headerStartRow: 0 },
    { name: "Desktop Profiles", selected: true, dataKey: "desktopProfiles", headerStartRow: 1 },
    { name: "Users", selected: true, dataKey: "users", headerStartRow: 1, headers: ["Object ID", "Last Name", "First Name", "Login Name / Email", "Default DN", "Site", "Supervisor", "User Profile", "Desktop Profile", "Skill Profile", "Multimedia Profile", "Team(s)"/* , "Description (Gemini)" */] },
    { name: "Prompts", selected: true, dataKey: null, headerStartRow: 1 },
    { name: "Queues", selected: true, dataKey: "queues", headerStartRow: 1 },
    { name: "Queues Formulas", selected: true, dataKey: null, headerStartRow: 0 },
    { name: "Skill Definitions", selected: true, dataKey: "skillDefinitions", headerStartRow: 1 },
    { name: "Skill Profiles", selected: true, dataKey: "skillProfiles", headerStartRow: 1 },
    { name: "Skill Profiles Fomulas", selected: true, dataKey: null, headerStartRow: 0 },
    { name: "Outbound ANI", selected: true, dataKey: "outdialAnis", headerStartRow: 1 },
    { name: "Outbound ANI Entries", selected: true, dataKey: "outdialAniEntries", headerStartRow: 1, headers: ["Object ID", "OutDial ANI Name", "Entry Name", "Entry Number"/* , "Description (Gemini)" */] },
    { name: "Aux Codes", selected: true, dataKey: "auxCodes", headerStartRow: 1 },
    { name: "User Profiles", selected: true, dataKey: "userProfiles", headerStartRow: 1 },
    { name: "Address Books", selected: true, dataKey: "addressBooks", headerStartRow: 1 },
    { name: "Address Book Entries", selected: true, dataKey: "addressBookEntries", headerStartRow: 1, headers: ["Object ID", "Address Book Name", "Address Book Entry Name", "Address Book Entry Number"/* , "Description (Gemini)" */] },
    { name: "Desktop Layouts", selected: true, dataKey: null, headerStartRow: 2 },
    { name: "Global Variables", selected: true, dataKey: "globalVariables", headerStartRow: 1 },
    { name: "GV_Formulas", selected: true, dataKey: null, headerStartRow: 0 },
    { name: "Chat Settings", selected: true, dataKey: null, headerStartRow: 2 },
    { name: "External Data Integrations", selected: true, dataKey: null, headerStartRow: 3 },
    { name: "Call Flows", selected: true, dataKey: null, headerStartRow: 0 },
    { name: "Multimedia Profiles", selected: true, dataKey: "multimediaProfiles", headerStartRow: 1 },
    { name: "Teams", selected: true, dataKey: "teams", headerStartRow: 1 },
    { name: "GDF Agent", selected: true, dataKey: null, headerStartRow: 2 },
    { name: "GDF Intents", selected: true, dataKey: null, headerStartRow: 2 },
    { name: "GDF Entities", selected: true, dataKey: null, headerStartRow: 3 },
    { name: "Voice Infrastructure", selected: true, dataKey: null, headerStartRow: 3 },
    { name: "Additional Notes", selected: true, dataKey: null, headerStartRow: 2 },
    { name: "Work Types", selected: true, dataKey: "workTypes", headerStartRow: 0 },
  ];

  const [sheetConfigurations, setSheetConfigurations] = useState(initialSheetSelection);

  // Debug useEffect to monitor fetchedData changes
  useEffect(() => {
    const fetchedKeys = Object.keys(fetchedData);
    if (fetchedKeys.length > 0) {
      updateProgress("info", `FetchedData updated: ${fetchedKeys.length} data sets available: ${fetchedKeys.join(', ')}`);
    }
  }, [fetchedData]);

  // Update download button state based on loading, data, and sheet selection
  useEffect(() => {
    const selectedSheets = sheetConfigurations.filter(s => s.selected).length;
    const downloadEnabled = !loading && Object.keys(fetchedData).length > 0 && selectedSheets > 0;
    setCanDownload(downloadEnabled);
    updateProgress("info", `Button state: loading=${loading}, fetchedData=${Object.keys(fetchedData).length}, selectedSheets=${selectedSheets}, canDownload=${downloadEnabled}`);
  }, [loading, fetchedData, sheetConfigurations]);

  const { org_id, access_token, site_id, timezone } = authData || {}; // authData might be null if no auth, use default empty object

  // Enhanced Data Collection Pipeline with Dependencies
  const dataCollectionSteps = {
    // Core foundational data (no dependencies)
    sites: {
      name: "Sites",
      description: "Collect site information",
      apiCall: getSites,
      dataKey: "sites",
      dependencies: [],
      priority: 1,
      required: true,
      completed: false,
      data: null,
      lookupFields: ["id", "name"]
    },
    globalVariables: {
      name: "Global Variables",
      description: "Collect global variable definitions",
      apiCall: getGlobalVariables,
      dataKey: "globalVariables",
      dependencies: [],
      priority: 1,
      required: false,
      completed: false,
      data: null,
      lookupFields: ["id", "name"]
    },
    businessHours: {
      name: "Business Hours",
      description: "Collect business hours schedules",
      apiCall: getBusinessHours,
      dataKey: "businessHours",
      dependencies: [],
      priority: 2,
      required: false,
      completed: false,
      data: null,
      lookupFields: ["id", "name"]
    },
    holidayLists: {
      name: "Holiday Lists",
      description: "Collect holiday list definitions",
      apiCall: getHolidayList,
      dataKey: "holidayLists",
      dependencies: [],
      priority: 2,
      required: false,
      completed: false,
      data: null,
      lookupFields: ["id", "holidayListName"]
    },
    overrides: {
      name: "Overrides",
      description: "Collect override list definitions",
      apiCall: getOverrides,
      dataKey: "overrides",
      dependencies: [],
      priority: 2,
      required: false,
      completed: false,
      data: null,
      lookupFields: ["id", "overrideListName"]
    },
    // Skill-related data (foundational for teams/users)
    skillDefinitions: {
      name: "Skill Definitions",
      description: "Collect skill definition data",
      apiCall: getSkillDefinitions,
      dataKey: "skillDefinitions",
      dependencies: [],
      priority: 3,
      required: true,
      completed: false,
      data: null,
      lookupFields: ["id", "name"]
    },
    skillProfiles: {
      name: "Skill Profiles",
      description: "Collect skill profile configurations",
      apiCall: getSkillProfiles,
      dataKey: "skillProfiles",
      dependencies: ["skillDefinitions"],
      priority: 4,
      required: true,
      completed: false,
      data: null,
      lookupFields: ["id", "name", "skillDefinitionId"]
    },
    // Media and profile data
    multimediaProfiles: {
      name: "Multimedia Profiles",
      description: "Collect multimedia profile definitions",
      apiCall: getMultimediaProfiles,
      dataKey: "multimediaProfiles",
      dependencies: [],
      priority: 3,
      required: false,
      completed: false,
      data: null,
      lookupFields: ["id", "name"]
    },
    desktopProfiles: {
      name: "Desktop Profiles",
      description: "Collect desktop profile configurations",
      apiCall: getDesktopProfiles,
      dataKey: "desktopProfiles",
      dependencies: [],
      priority: 3,
      required: false,
      completed: false,
      data: null,
      lookupFields: ["id", "name"]
    },
    userProfiles: {
      name: "User Profiles",
      description: "Collect user profile definitions",
      apiCall: getUserProfiles,
      dataKey: "userProfiles",
      dependencies: ["sites"],
      priority: 4,
      required: false,
      completed: false,
      data: null,
      lookupFields: ["id", "name"]
    },
    // Team data (depends on sites, multimedia profiles, skill profiles)
    teams: {
      name: "Teams",
      description: "Collect team configurations",
      apiCall: getTeams,
      dataKey: "teams",
      dependencies: ["sites", "multimediaProfiles", "skillProfiles"],
      priority: 5,
      required: true,
      completed: false,
      data: null,
      lookupFields: ["id", "name", "siteId", "mediaProfileId", "skillProfileId"]
    },
    // Queue data (depends on teams)
    queues: {
      name: "Queues",
      description: "Collect queue configurations",
      apiCall: getQueues,
      dataKey: "queues",
      dependencies: ["teams"],
      priority: 6,
      required: true,
      completed: false,
      data: null,
      lookupFields: ["id", "name", "callDistributionGroups"]
    },
    // Entry Points and Dialed Numbers
    entryPoints: {
      name: "Entry Points",
      description: "Collect entry point definitions",
      apiCall: getEntryPoints,
      dataKey: "entryPoints",
      dependencies: [],
      priority: 4,
      required: false,
      completed: false,
      data: null,
      lookupFields: ["id", "name"]
    },
    // Dialed Number Mappings (can be processed independently)
    dialedNumberMappings: {
      name: "Dialed Number Mappings",
      description: "Collect dialed number mapping configurations (API may not be available)",
      apiCall: getDialedNumberMappings,
      dataKey: "dialedNumberMappings",
      dependencies: [],
      priority: 4,
      required: false,
      completed: false,
      data: null,
      lookupFields: ["id", "number", "entryPointId"],
      optional: true // Mark as optional due to API availability issues
    },
    // Address Books (can be processed independently)
    addressBooks: {
      name: "Address Books",
      description: "Collect address book definitions",
      apiCall: getAddressBook,
      dataKey: "addressBooks",
      dependencies: [],
      priority: 3,
      required: false,
      completed: false,
      data: null,
      lookupFields: ["id", "name"]
    },
    addressBookEntries: {
      name: "Address Book Entries",
      description: "Collect address book entry data",
      apiCall: null, // Special handling required
      dataKey: "addressBookEntries",
      dependencies: ["addressBooks"],
      priority: 4,
      required: false,
      completed: false,
      data: null,
      lookupFields: ["id", "name", "addressBookName"]
    },
    // Auxiliary data
    auxCodes: {
      name: "Auxiliary Codes",
      description: "Collect auxiliary code definitions",
      apiCall: getAuxCodes,
      dataKey: "auxCodes",
      dependencies: [],
      priority: 3,
      required: false,
      completed: false,
      data: null,
      lookupFields: ["id", "name"]
    },
    workTypes: {
      name: "Work Types",
      description: "Collect work type definitions",
      apiCall: getWorkTypes,
      dataKey: "workTypes",
      dependencies: [],
      priority: 2,
      required: false,
      completed: false,
      data: null,
      lookupFields: ["id", "name"]
    },
    // OutDial ANI data (can be processed independently)
    outdialAnis: {
      name: "OutDial ANIs",
      description: "Collect OutDial ANI definitions",
      apiCall: getOutdialAnis,
      dataKey: "outdialAnis",
      dependencies: [],
      priority: 3,
      required: false,
      completed: false,
      data: null,
      lookupFields: ["id", "name"]
    },
    outdialAniEntries: {
      name: "OutDial ANI Entries",
      description: "Collect OutDial ANI entry data",
      apiCall: null, // Special handling required
      dataKey: "outdialAniEntries",
      dependencies: ["outdialAnis"],
      priority: 4,
      required: false,
      completed: false,
      data: null,
      lookupFields: ["id", "name", "outdialAniName"]
    },
    // User data (depends on most other entities)
    users: {
      name: "Users",
      description: "Collect user account information",
      apiCall: getUsersList,
      dataKey: "users",
      dependencies: ["sites", "teams", "userProfiles", "desktopProfiles", "skillProfiles", "multimediaProfiles"],
      priority: 7,
      required: false,
      completed: false,
      data: null,
      lookupFields: ["id", "email", "firstName", "lastName", "siteId", "userProfileId", "desktopProfileId", "skillProfileId", "multimediaProfileId", "teams"]
    }
  };

  // Function to scroll to the bottom of the log
  const scrollToBottom = () => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  };

  // Enhanced progress tracking system
  const updateProgress = (type, message, stepName = null, extraData = {}) => {
    const progressEntry = {
      type,
      message,
      stepName,
      timestamp: new Date().toLocaleTimeString(),
      fullTimestamp: new Date().toISOString(),
      memoryUsage: performance.memory?.usedJSHeapSize || 0,
      ...extraData
    };

    setProgress((prev) => {
      const newProgress = [...prev, progressEntry];
      return newProgress;
    });

    // Update step-specific progress tracking
    if (stepName && collectedData[stepName]) {
      setCollectedData(prev => ({
        ...prev,
        [stepName]: {
          ...prev[stepName],
          lastActivity: progressEntry.timestamp,
          messages: [...(prev[stepName].messages || []), progressEntry]
        }
      }));
    }

    // Scroll to bottom after state update
    setTimeout(scrollToBottom, 0);
  };

  // Helper function to add step-specific progress
  const addStepProgress = (stepName, message, type = "info", extraData = {}) => {
    updateProgress(type, message, stepName, {
      stepData: collectedData[stepName],
      ...extraData
    });
  };

  // Helper function to calculate overall progress
  const calculateProgress = () => {
    const totalSteps = Object.keys(dataCollectionSteps).length;
    const completedSteps = Object.values(dataCollectionSteps).filter(step => step.completed).length;
    const percentage = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;

    return {
      completed: completedSteps,
      total: totalSteps,
      percentage,
      current: currentStep
    };
  };

  // Helper function to check if step dependencies are satisfied
  const areDependenciesSatisfied = (stepName) => {
    const step = dataCollectionSteps[stepName];
    if (!step || !step.dependencies || step.dependencies.length === 0) {
      return true;
    }

    return step.dependencies.every(depName => {
      const dependency = dataCollectionSteps[depName];
      const isCompleted = dependency && dependency.completed;
      if (!isCompleted) {
        updateProgress("warning",
          `Step "${stepName}" waiting for dependency "${depName}" to complete`,
          stepName
        );
      }
      return isCompleted;
    });
  };

  // Helper function to get next available steps
  const getNextAvailableSteps = () => {
    return Object.entries(dataCollectionSteps)
      .filter(([stepName, step]) =>
        !step.completed &&
        areDependenciesSatisfied(stepName)
      )
      .sort(([, a], [, b]) => a.priority - b.priority)
      .map(([stepName]) => stepName);
  };

  // Styles for progress messages
  const getMessageStyle = (type) => {
    switch (type) {
      case "info":
        return { color: "#2196f3" }; // Blue
      case "success":
        return { color: "#4caf50" }; // Green
      case "warning":
        return { color: "#ff9800" }; // Orange
      case "error":
        return { color: "#f44336" }; // Red
      default:
        return { color: "#333" }; // Black/default
    }
  };

  // Cross-reference data resolution system
  const crossReferenceCache = useRef(new Map()); // Cache for lookup performance

  /**
   * Build lookup maps for cross-referencing data
   */
  const buildLookupMaps = () => {
    updateProgress("info", "Building cross-reference lookup maps");
    const lookupMaps = {};

    Object.entries(dataCollectionSteps).forEach(([stepName, stepData]) => {
      if (stepData.completed && stepData.data && Array.isArray(stepData.data)) {
        const step = dataCollectionSteps[stepName];
        if (step && step.lookupFields) {
          // Create multiple lookup maps for this step's data
          step.lookupFields.forEach(field => {
            const mapKey = `${stepName}_by_${field}`;
            lookupMaps[mapKey] = new Map();

            stepData.data.forEach(item => {
              if (item[field] !== undefined && item[field] !== null) {
                lookupMaps[mapKey].set(item[field], item);
              }
            });
          });
        }
      }
    });

    crossReferenceCache.current = lookupMaps;
    updateProgress("success",
      `Built ${Object.keys(lookupMaps).length} lookup maps for cross-referencing`,
      null,
      { lookupMapCount: Object.keys(lookupMaps).length }
    );

    return lookupMaps;
  };

  /**
   * Resolve cross-references for a given entity
   */
  const resolveCrossReferences = (entity, entityType, lookupMaps = null) => {
    if (!lookupMaps) {
      lookupMaps = crossReferenceCache.current;
    }

    const resolvedEntity = { ...entity };

    // Define resolution patterns based on entity types
    const resolutionPatterns = {
      teams: {
        siteId: { sourceStep: "sites", targetField: "name", resolvedField: "siteName" },
        mediaProfileId: { sourceStep: "multimediaProfiles", targetField: "name", resolvedField: "mediaProfileName" },
        skillProfileId: { sourceStep: "skillProfiles", targetField: "name", resolvedField: "skillProfileName" }
      },
      users: {
        siteId: { sourceStep: "sites", targetField: "name", resolvedField: "siteName" },
        userProfileId: { sourceStep: "userProfiles", targetField: "name", resolvedField: "userProfileName" },
        desktopProfileId: { sourceStep: "desktopProfiles", targetField: "name", resolvedField: "desktopProfileName" },
        skillProfileId: { sourceStep: "skillProfiles", targetField: "name", resolvedField: "skillProfileName" },
        multimediaProfileId: { sourceStep: "multimediaProfiles", targetField: "name", resolvedField: "multimediaProfileName" }
      },
      queues: {
        callDistributionGroups: {
          sourceStep: "teams",
          targetField: "name",
          resolvedField: "teamNames",
          isArray: true,
          arrayIdField: "id"
        }
      },
      skillProfiles: {
        skillDefinitionId: { sourceStep: "skillDefinitions", targetField: "name", resolvedField: "skillDefinitionName" }
      },
      businessHours: {
        holidayListId: { sourceStep: "holidayLists", targetField: "holidayListName", resolvedField: "holidayListName" },
        overrideListId: { sourceStep: "overrides", targetField: "overrideListName", resolvedField: "overrideListName" }
      },
      entryPoints: {
        // Add entry point specific resolutions if needed
      },
      dialedNumberMappings: {
        entryPointId: { sourceStep: "entryPoints", targetField: "name", resolvedField: "entryPointName" }
      },
      addressBookEntries: {
        // These already have addressBookName from processing
      },
      outdialAniEntries: {
        // These already have outdialAniName from processing
      }
    };

    const patterns = resolutionPatterns[entityType];
    if (!patterns) {
      return resolvedEntity;
    }

    Object.entries(patterns).forEach(([sourceField, config]) => {
      const sourceValue = entity[sourceField];
      if (sourceValue === undefined || sourceValue === null) {
        return;
      }

      const mapKey = `${config.sourceStep}_by_id`;
      const lookupMap = lookupMaps[mapKey];

      if (!lookupMap) {
        return;
      }

      if (config.isArray && Array.isArray(sourceValue)) {
        // Handle array of references (like teams in queues)
        const resolvedArray = sourceValue.map(item => {
          const idValue = item[config.arrayIdField] || item.id || item;
          const referencedEntity = lookupMap.get(idValue);
          return referencedEntity ? referencedEntity[config.targetField] : item[config.targetField] || idValue;
        }).filter(Boolean);

        resolvedEntity[config.resolvedField] = resolvedArray.join(', ');
      } else {
        // Handle single reference
        const referencedEntity = lookupMap.get(sourceValue);
        if (referencedEntity) {
          resolvedEntity[config.resolvedField] = referencedEntity[config.targetField];
        }
      }
    });

    return resolvedEntity;
  };

  /**
   * Resolve all cross-references in collected data
   */
  const resolveAllCrossReferences = () => {
    updateProgress("info", "Resolving cross-references in collected data");
    const lookupMaps = buildLookupMaps();
    let totalResolved = 0;

    Object.entries(dataCollectionSteps).forEach(([stepName, stepData]) => {
      if (stepData.completed && stepData.data && Array.isArray(stepData.data)) {
        const resolvedData = stepData.data.map(entity => {
          const resolved = resolveCrossReferences(entity, stepName, lookupMaps);
          totalResolved++;
          return resolved;
        });

        // Store resolved data back to the step data
        dataCollectionSteps[stepName].resolvedData = resolvedData;
        dataCollectionSteps[stepName].crossReferencesResolved = true;

        setCollectedData(prev => ({
          ...prev,
          [stepName]: {
            ...prev[stepName],
            resolvedData,
            crossReferencesResolved: true
          }
        }));
      }
    });

    updateProgress("success",
      `Resolved cross-references for ${totalResolved} entities`,
      null,
      { resolvedCount: totalResolved }
    );

    return totalResolved;
  };

  // Dynamic Column Generation System

  /**
   * Analyze API response data to determine optimal column structure
   * @param {Array} apiData - The raw API response data
   * @param {string} entityType - Type of entity for context
   * @returns {Array} - Array of column definitions
   */
  const analyzeDataStructure = (apiData, entityType) => {
    if (!apiData || !Array.isArray(apiData) || apiData.length === 0) {
      return [];
    }

    // Get all unique keys from the data
    const allKeys = new Set();
    const keyMetadata = new Map();

    apiData.forEach(item => {
      Object.keys(item).forEach(key => {
        allKeys.add(key);

        if (!keyMetadata.has(key)) {
          keyMetadata.set(key, {
            type: typeof item[key],
            samples: [],
            isArray: false,
            isObject: false,
            nullCount: 0,
            totalCount: 0
          });
        }

        const metadata = keyMetadata.get(key);
        metadata.totalCount++;

        if (item[key] === null || item[key] === undefined) {
          metadata.nullCount++;
        } else {
          metadata.samples.push(item[key]);
          if (Array.isArray(item[key])) {
            metadata.isArray = true;
          } else if (typeof item[key] === 'object') {
            metadata.isObject = true;
          }
        }
      });
    });

    // Define priority fields for common entity types
    const priorityFields = {
      sites: ['id', 'name', 'mediaProfileId', 'mediaProfileName'],
      teams: ['id', 'name', 'type', 'siteId', 'siteName', 'mediaProfileId', 'mediaProfileName', 'skillProfileId', 'skillProfileName'],
      users: ['id', 'firstName', 'lastName', 'email', 'loginName', 'siteId', 'siteName', 'userProfileId', 'desktopProfileId', 'skillProfileId', 'multimediaProfileId', 'teams'],
      queues: ['id', 'name', 'direction', 'channelType', 'routingType', 'serviceLevelThreshold', 'maxTimeInQueue', 'callDistributionGroups', 'teamNames'],
      skillDefinitions: ['id', 'name', 'description', 'type', 'serviceLevelThreshold', 'enumValues'],
      skillProfiles: ['id', 'name', 'skillDefinitionId', 'skillDefinitionName', 'type', 'value'],
      entryPoints: ['id', 'name', 'description', 'direction', 'channel', 'assetId', 'serviceLevelThreshold'],
      dialedNumberMappings: ['id', 'number', 'description', 'entryPointId', 'maxActiveCalls', 'type'],
      addressBooks: ['id', 'name', 'description', 'type'],
      addressBookEntries: ['id', 'addressBookName', 'name', 'number'],
      outdialAnis: ['id', 'name', 'description', 'type'],
      outdialAniEntries: ['id', 'outdialAniName', 'name', 'number'],
      businessHours: ['id', 'name', 'description', 'dayOfWeek', 'startTime', 'endTime', 'holidayListId', 'overrideListId'],
      desktopProfiles: ['id', 'name', 'type', 'screenPops', 'lastAgentRouting', 'autoAnswer', 'wrapUpType', 'wrapUpTimeSeconds'],
      multimediaProfiles: ['id', 'name', 'description', 'type', 'voiceCount', 'chatCount', 'emailCount', 'socialCount'],
      auxCodes: ['id', 'name', 'description', 'type'],
      userProfiles: ['id', 'name', 'profileType', 'entryPointAccess', 'siteAccess', 'queueAccess', 'teamAccess'],
      globalVariables: ['id', 'name', 'description', 'type', 'defaultValue', 'agentViewable', 'agentEditable', 'reportable'],
      workTypes: ['id', 'name', 'description']
    };

    const entityPriorities = priorityFields[entityType] || [];

    // Create column definitions
    const columns = [];
    const processedKeys = new Set();

    // First, add priority fields in order
    entityPriorities.forEach(priorityKey => {
      if (allKeys.has(priorityKey)) {
        const metadata = keyMetadata.get(priorityKey);
        columns.push({
          key: priorityKey,
          header: formatColumnHeader(priorityKey),
          type: metadata.type,
          isArray: metadata.isArray,
          isObject: metadata.isObject,
          priority: true,
          nullPercentage: (metadata.nullCount / metadata.totalCount) * 100
        });
        processedKeys.add(priorityKey);
      }
    });

    // Then add remaining fields, filtering out less useful ones
    Array.from(allKeys)
      .filter(key => !processedKeys.has(key))
      .filter(key => {
        const metadata = keyMetadata.get(key);
        const nullPercentage = (metadata.nullCount / metadata.totalCount) * 100;
        // Filter out fields that are mostly null or are complex objects
        return nullPercentage < 90 && !metadata.isObject;
      })
      .sort((a, b) => {
        const metaA = keyMetadata.get(a);
        const metaB = keyMetadata.get(b);
        const nullPercentageA = (metaA.nullCount / metaA.totalCount) * 100;
        const nullPercentageB = (metaB.nullCount / metaB.totalCount) * 100;
        // Sort by usefulness: less nulls first, then alphabetically
        return nullPercentageA - nullPercentageB || a.localeCompare(b);
      })
      .forEach(key => {
        const metadata = keyMetadata.get(key);
        columns.push({
          key,
          header: formatColumnHeader(key),
          type: metadata.type,
          isArray: metadata.isArray,
          isObject: metadata.isObject,
          priority: false,
          nullPercentage: (metadata.nullCount / metadata.totalCount) * 100
        });
      });

    return columns;
  };

  /**
   * Format field names into readable column headers
   * @param {string} fieldName - Raw field name from API
   * @returns {string} - Formatted column header
   */
  const formatColumnHeader = (fieldName) => {
    // Handle common patterns
    const headerMappings = {
      'id': 'ID',
      'firstName': 'First Name',
      'lastName': 'Last Name',
      'loginName': 'Login Name',
      'siteName': 'Site Name',
      'siteId': 'Site ID',
      'mediaProfileId': 'Media Profile ID',
      'mediaProfileName': 'Media Profile Name',
      'skillProfileId': 'Skill Profile ID',
      'skillProfileName': 'Skill Profile Name',
      'userProfileId': 'User Profile ID',
      'desktopProfileId': 'Desktop Profile ID',
      'multimediaProfileId': 'Multimedia Profile ID',
      'skillDefinitionId': 'Skill Definition ID',
      'skillDefinitionName': 'Skill Definition Name',
      'callDistributionGroups': 'Call Distribution Groups',
      'teamNames': 'Team Names',
      'channelType': 'Channel Type',
      'routingType': 'Routing Type',
      'serviceLevelThreshold': 'Service Level Threshold',
      'maxTimeInQueue': 'Max Time In Queue',
      'enumValues': 'Enum Values',
      'dayOfWeek': 'Day of Week',
      'startTime': 'Start Time',
      'endTime': 'End Time',
      'holidayListId': 'Holiday List ID',
      'overrideListId': 'Override List ID',
      'screenPops': 'Screen Pops',
      'lastAgentRouting': 'Last Agent Routing',
      'autoAnswer': 'Auto Answer',
      'wrapUpType': 'Wrap Up Type',
      'wrapUpTimeSeconds': 'Wrap Up Time (Seconds)',
      'voiceCount': 'Voice Count',
      'chatCount': 'Chat Count',
      'emailCount': 'Email Count',
      'socialCount': 'Social Count',
      'profileType': 'Profile Type',
      'entryPointAccess': 'Entry Point Access',
      'siteAccess': 'Site Access',
      'queueAccess': 'Queue Access',
      'teamAccess': 'Team Access',
      'defaultValue': 'Default Value',
      'agentViewable': 'Agent Viewable',
      'agentEditable': 'Agent Editable',
      'addressBookName': 'Address Book Name'
    };

    if (headerMappings[fieldName]) {
      return headerMappings[fieldName];
    }

    // Convert camelCase to Title Case
    return fieldName
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  };

  /**
   * Fetches data for a specific entity type from the Webex CX API.
   * @param {Function} apiCall - The API function to call.
   * @param {string} entityName - The name of the entity (e.g., "Teams").
   * @param {Array<any>} apiCallArgs - An array of arguments to pass to the apiCall function, excluding setProgress.
   * @returns {Promise<Array>} - A promise that resolves to an array of fetched entities.
   */
  const fetchData = async (apiCall, entityName, ...apiCallArgs) => {
    // Only attempt to fetch if authData is available
    if (!org_id || !access_token) {
      updateProgress("warning", `Skipping fetching ${entityName}: Authentication data is missing.`);
      return [];
    }
    updateProgress("info", `Fetching ${entityName}...`);
    try {
      // Pass all provided arguments followed by updateProgress
      const data = await apiCall(...apiCallArgs, org_id, access_token, updateProgress);
      updateProgress("success", `Successfully fetched ${data.length} ${entityName}.`);
      return data;
    } catch (error) {
      updateProgress("error", `Failed to fetch ${entityName}: ${error.message || String(error)}`);
      return []; // Return empty array on error to prevent breaking subsequent steps
    }
  };

  /**
   * Identifies objects that are not referenced by other relevant objects.
   * @param {object} allFetchedData - All data fetched from the APIs.
   * @returns {Array<Array>} - An array of arrays, each representing an unreferenced object's details.
   */
  const findUnreferencedObjects = (allFetchedData) => {
    const unreferenced = [];

    // Helper to check if an ID exists in a list of objects
    const isReferenced = (id, referrers, fieldName = 'id') => {
      return referrers?.some(referrer => referrer[fieldName] === id);
    };

    // Helper to check if an ID exists in an array of objects within a list
    const isReferencedInArray = (id, referrers, arrayFieldName, idFieldName = 'id') => {
      return referrers?.some(referrer => 
        referrer[arrayFieldName]?.some(item => item[idFieldName] === id)
      );
    };

    // Skill Definitions
    if (allFetchedData.skillDefinitions && allFetchedData.skillProfiles) {
      allFetchedData.skillDefinitions.forEach(sd => {
        if (!isReferenced(sd.id, allFetchedData.skillProfiles, 'skillDefinitionId')) {
          unreferenced.push(["Skill Definition", sd.name, sd.id, "Not referenced by any Skill Profile."]);
        }
      });
    }

    // Multimedia Profiles
    if (allFetchedData.multimediaProfiles) {
      allFetchedData.multimediaProfiles.forEach(mp => {
        const referencedBySite = isReferenced(mp.id, allFetchedData.sites, 'mediaProfileId');
        const referencedByTeam = isReferenced(mp.id, allFetchedData.teams, 'mediaProfileId');
        const referencedByUser = isReferenced(mp.id, allFetchedData.users, 'multimediaProfileId'); // Assuming users can directly reference multimedia profiles
        if (!referencedBySite && !referencedByTeam && !referencedByUser) {
          unreferenced.push(["Multimedia Profile", mp.name, mp.id, "Not referenced by any Site, Team, or User."]);
        }
      });
    }

    // Desktop Profiles
    if (allFetchedData.desktopProfiles && allFetchedData.users) {
      allFetchedData.desktopProfiles.forEach(dp => {
        if (!isReferenced(dp.id, allFetchedData.users, 'desktopProfileId')) {
          unreferenced.push(["Desktop Profile", dp.name, dp.id, "Not referenced by any User."]);
        }
      });
    }

    // Address Books (check if any entries exist for them)
    if (allFetchedData.addressBooks && allFetchedData.addressBookEntries) {
      allFetchedData.addressBooks.forEach(ab => {
        const hasEntries = allFetchedData.addressBookEntries.some(entry => entry.addressBookName === ab.name); // Using name as `addressBookId` might not be directly available on entry
        if (!hasEntries) {
          unreferenced.push(["Address Book", ab.name, ab.id, "No entries found in this Address Book."]);
        }
      });
    }
    
    // Entry Points
    if (allFetchedData.entryPoints) {
        allFetchedData.entryPoints.forEach(ep => {
            const referencedByDNM = isReferenced(ep.id, allFetchedData.dialedNumberMappings, 'entryPointId');
            const referencedByUserProfile = isReferencedInArray(ep.id, allFetchedData.userProfiles, 'entryPoints'); // Assuming userProfiles can reference entry points directly by ID in an array
            // Add more checks if other objects reference Entry Points
            if (!referencedByDNM && !referencedByUserProfile) {
                unreferenced.push(["Entry Point", ep.name, ep.id, "Not referenced by any Dialed Number Mapping or User Profile."]);
            }
        });
    }

    // Aux Codes (often standalone, but if linked by API, add checks)
    // For now, consider them referenced if they exist, as direct links might not be common in API structure
    // If there's an API that links to Aux Codes, add a check here.
    
    // User Profiles
    if (allFetchedData.userProfiles && allFetchedData.users) {
      allFetchedData.userProfiles.forEach(up => {
        if (!isReferenced(up.id, allFetchedData.users, 'userProfileId')) {
          unreferenced.push(["User Profile", up.name, up.id, "Not assigned to any User."]);
        }
      });
    }

    // Global Variables (often standalone, add checks if linked by API)
    // If there's an API that links to Global Variables, add a check here.

    // Sites (referenced by Teams, Users, Entry Points)
    if (allFetchedData.sites) {
        allFetchedData.sites.forEach(site => {
            const referencedByTeam = isReferenced(site.id, allFetchedData.teams, 'siteId');
            const referencedByUser = isReferenced(site.id, allFetchedData.users, 'siteId');
            const referencedByUserProfile = isReferencedInArray(site.id, allFetchedData.userProfiles, 'sites');
            // Add more checks if other objects reference Sites
            if (!referencedByTeam && !referencedByUser && !referencedByUserProfile) {
                unreferenced.push(["Site", site.name, site.id, "Not referenced by any Team, User, or User Profile."]);
            }
        });
    }

    // Holiday Lists
    if (allFetchedData.holidayLists && allFetchedData.businessHours) {
        allFetchedData.holidayLists.forEach(hl => {
            if (!isReferenced(hl.id, allFetchedData.businessHours, 'holidayListId')) {
                unreferenced.push(["Holiday List", hl.holidayListName, hl.id, "Not referenced by any Business Hours schedule."]);
            }
        });
    }

    // Overrides
    if (allFetchedData.overrides && allFetchedData.businessHours) {
        allFetchedData.overrides.forEach(ov => {
            if (!isReferenced(ov.id, allFetchedData.businessHours, 'overrideListId')) {
                unreferenced.push(["Override List", ov.overrideListName, ov.id, "Not referenced by any Business Hours schedule."]);
            }
        });
    }

    // Business Hours (referenced by Entry Points or Queues, if applicable)
    // This requires knowing how Business Hours are applied in call flows.
    // For now, if they are not referenced by themselves (holidays/overrides), they are considered.
    // If Entry Points or Queues have a direct link to Business Hours, add checks here.

    return unreferenced;
  };


  /**
   * Maps fetched API data to the structure required for an Excel sheet using dynamic column generation.
   * @param {string} sheetName - The name of the Excel sheet.
   * @param {Array} apiData - The data fetched from the API.
   * @returns {Promise<Object>} - Object containing both headers and data rows for dynamic column generation.
   */
  const mapDataToExcelSheet = async (sheetName, apiData) => {
    // Handle special cases that don't use dynamic columns
    if (sheetName === "Summary") {
      return {
        headers: ["Category", "Count", "Notes"],
        rows: [
          ["Total Queues", fetchedData.queues?.length || 0, ""],
          ["Total Teams", fetchedData.teams?.length || 0, ""],
          ["Total Users", fetchedData.users?.length || 0, ""],
          ["Total Skill Definitions", fetchedData.skillDefinitions?.length || 0, ""],
          ["Total Skill Profiles", fetchedData.skillProfiles?.length || 0, ""],
          ["Total Entry Points", fetchedData.entryPoints?.length || 0, ""],
          ["Total Address Books", fetchedData.addressBooks?.length || 0, ""],
          ["Total Address Book Entries", fetchedData.addressBookEntries?.length || 0, ""],
          ["Total OutDial ANIs", fetchedData.outdialAnis?.length || 0, ""],
          ["Total OutDial ANI Entries", fetchedData.outdialAniEntries?.length || 0, ""],
          ["Total Business Hours Schedules", fetchedData.businessHours?.length || 0, ""],
          ["Total Holiday Lists", fetchedData.holidayLists?.length || 0, ""],
          ["Total Overrides", fetchedData.overrides?.length || 0, ""],
          ["Total Desktop Profiles", fetchedData.desktopProfiles?.length || 0, ""],
          ["Total Aux Codes", fetchedData.auxCodes?.length || 0, ""],
          ["Total User Profiles", fetchedData.userProfiles?.length || 0, ""],
          ["Total Multimedia Profiles", fetchedData.multimediaProfiles?.length || 0, ""],
          ["Total Global Variables", fetchedData.globalVariables?.length || 0, ""],
          ["Total Sites", fetchedData.sites?.length || 0, ""],
          ["Total Work Types", fetchedData.workTypes?.length || 0, ""],
        ]
      };
    }

    if (sheetName === "Unreferenced Objects") {
      return {
        headers: ["Object Type", "Object Name", "Object ID", "Notes"],
        rows: findUnreferencedObjects(fetchedData)
      };
    }

    // For dynamic data sheets, analyze the structure and generate columns
    if (!apiData || !Array.isArray(apiData) || apiData.length === 0) {
      updateProgress("warning", `No data available for sheet: ${sheetName}`);
      return { headers: [], rows: [] };
    }

    // Get entity type from sheet name mapping
    const entityTypeMapping = {
      "Skill Definitions": "skillDefinitions",
      "Skill Profiles": "skillProfiles",
      "Teams": "teams",
      "Work Types": "workTypes",
      "Queues": "queues",
      "Users": "users",
      "Desktop Profiles": "desktopProfiles",
      "Hours of Operation": "businessHours",
      "Holidays": "holidayLists",
      "Overrides": "overrides",
      "Address Books": "addressBooks",
      "Address Book Entries": "addressBookEntries",
      "Entry Points": "entryPoints",
      "Dialed Number Mappings": "dialedNumberMappings",
      "Outbound ANI": "outdialAnis",
      "Outbound ANI Entries": "outdialAniEntries",
      "Aux Codes": "auxCodes",
      "User Profiles": "userProfiles",
      "Multimedia Profiles": "multimediaProfiles",
      "Global Variables": "globalVariables",
      "Sites": "sites"
    };

    const entityType = entityTypeMapping[sheetName];
    if (!entityType) {
      updateProgress("warning", `Unknown entity type for sheet: ${sheetName}`);
      return { headers: [], rows: [] };
    }

    // Use resolved data if available for better cross-references
    const dataToAnalyze = dataCollectionSteps[entityType]?.resolvedData || apiData;

    // Analyze the data structure to determine columns
    const columnDefinitions = analyzeDataStructure(dataToAnalyze, entityType);

    updateProgress("info",
      `Generated ${columnDefinitions.length} dynamic columns for ${sheetName}: ${columnDefinitions.slice(0, 5).map(col => col.header).join(', ')}${columnDefinitions.length > 5 ? '...' : ''}`,
      entityType,
      {
        columnCount: columnDefinitions.length,
        dataCount: dataToAnalyze.length,
        columns: columnDefinitions.map(col => ({ key: col.key, header: col.header, priority: col.priority }))
      }
    );

    // Extract headers from column definitions
    const headers = columnDefinitions.map(col => col.header);

    // Generate data rows based on the column definitions
    const rows = dataToAnalyze.map(item => {
      return columnDefinitions.map(col => {
        const value = item[col.key];

        // Handle different data types appropriately
        if (value === null || value === undefined) {
          return "";
        } else if (col.isArray && Array.isArray(value)) {
          // For arrays, join them into a readable string
          return value.map(v => {
            if (v === null || v === undefined) {
              return '';
            } else if (typeof v === 'object' && v !== null) {
              if (v.name) return String(v.name);
              if (v.id) return String(v.id);
              // Stringify complex objects
              try {
                return JSON.stringify(v);
              } catch (e) {
                return String(v);
              }
            }
            return String(v);
          }).filter(Boolean).join(', ');
        } else if (typeof value === 'boolean') {
          return value ? 'Yes' : 'No';
        } else if (typeof value === 'object' && value !== null) {
          // For objects, try to extract meaningful representation
          if (value.name) {
            return String(value.name);
          } else if (value.id) {
            return String(value.id);
          }
          // Stringify the entire object to ensure it's readable
          try {
            return JSON.stringify(value);
          } catch (e) {
            // If JSON.stringify fails, convert to string
            return String(value);
          }
        } else {
          return String(value);
        }
      });
    });

    return { headers, rows };
  };

  /**
   * Runs validation checks on the fetched data and logs warnings/errors.
   */
  const runValidationChecks = () => {
    updateProgress("info", "Running validation checks on fetched data...");

    // Example 1: Check for queues without associated teams
    if (fetchedData.queues && fetchedData.teams) {
      fetchedData.queues.forEach(queue => {
        if (!queue.callDistributionGroups || queue.callDistributionGroups.length === 0) {
          updateProgress("warning", `Validation: Queue "${queue.name}" (ID: ${queue.id}) has no associated Call Distribution Groups (Teams).`);
        } else {
          const missingTeams = queue.callDistributionGroups.filter(groupId => !fetchedData.teams.some(team => team.id === groupId.id));
          if (missingTeams.length > 0) {
            updateProgress("warning", `Validation: Queue "${queue.name}" (ID: ${queue.id}) refers to non-existent Teams: ${missingTeams.map(t => t.id).join(', ')}.`);
          }
        }
      });
    }

    // Example 2: Check for skill profiles without associated skill definitions
    if (fetchedData.skillProfiles && fetchedData.skillDefinitions) {
      fetchedData.skillProfiles.forEach(skillProfile => {
        if (!skillProfile.skillDefinitionId) {
          updateProgress("warning", `Validation: Skill Profile "${skillProfile.name}" (ID: ${skillProfile.id}) has no associated Skill Definition.`);
        } else {
          const skillDefExists = fetchedData.skillDefinitions.some(sd => sd.id === skillProfile.skillDefinitionId);
          if (!skillDefExists) {
            updateProgress("warning", `Validation: Skill Profile "${skillProfile.name}" (ID: ${skillProfile.id}) refers to a non-existent Skill Definition (ID: ${skillProfile.skillDefinitionId}).`);
          }
        }
      });
    }

    // Example 3: Check for duplicate names (simple check, can be expanded)
    const checkDuplicates = (items, type, nameField = 'name') => {
      if (!items) return;
      const names = items.map(item => item[nameField]).filter(Boolean);
      const duplicates = names.filter((name, index) => names.indexOf(name) !== index);
      if (duplicates.length > 0) {
        updateProgress("warning", `Validation: Duplicate ${type} names found: ${[...new Set(duplicates)].join(', ')}.`);
      }
    };

    checkDuplicates(fetchedData.queues, "Queue");
    checkDuplicates(fetchedData.teams, "Team");
    checkDuplicates(fetchedData.skillDefinitions, "Skill Definition");
    checkDuplicates(fetchedData.skillProfiles, "Skill Profile");
    checkDuplicates(fetchedData.users, "User", 'email'); // Assuming email is unique for users
    checkDuplicates(fetchedData.addressBooks, "Address Book");
    
    updateProgress("info", "Validation checks completed.");
  };

  const handleGenerateAsBuilt = async () => {
    setLoading(true);
    setCanDownload(false); // Explicitly disable download button during generation
    setProgress([]);
    setExportProgress(0);
    setCollectedData({});
    setCurrentStep(null);
    setCompletedSteps([]);

    try {
      updateProgress("info", "Starting enhanced As-Built data generation with dependency management...");

      if (!org_id || !access_token) {
        updateProgress("error", "Cannot generate As-Built data: Authentication data (Org ID and Access Token) is missing. Please authenticate first.");
        return;
      }

      // Initialize collection tracking
      const initCollectedData = {};
      Object.entries(dataCollectionSteps).forEach(([stepName, step]) => {
        initCollectedData[stepName] = {
          ...step,
          messages: [],
          startTime: null,
          endTime: null,
          duration: 0
        };
      });
      setCollectedData(initCollectedData);

      const progressInfo = calculateProgress();
      setTotalSteps(progressInfo.total);
      updateProgress("info", `Initialized ${progressInfo.total} data collection steps`);

      // Process steps in dependency order
      const processedSteps = new Set();
      const maxRetries = 3;
      let retryCount = 0;

      while (processedSteps.size < Object.keys(dataCollectionSteps).length && retryCount < maxRetries) {
        const availableSteps = getNextAvailableSteps().filter(step => !processedSteps.has(step));

        if (availableSteps.length === 0) {
          retryCount++;
          updateProgress("warning", `No available steps to process. Retry ${retryCount}/${maxRetries}`);
          continue;
        }

        // Process available steps in parallel (within reason)
        const batchSize = Math.min(3, availableSteps.length); // Limit parallel processing
        const batch = availableSteps.slice(0, batchSize);

        updateProgress("info", `Processing batch of ${batch.length} steps: ${batch.join(', ')}`);

        await Promise.all(batch.map(async (stepName) => {
        const step = dataCollectionSteps[stepName];
        setCurrentStep(stepName);

        const startTime = Date.now();
        addStepProgress(stepName, `Starting ${step.name} data collection`, "info");

        try {
          let stepData = null;

          // Special handling for address book entries and outdial ANI entries
          if (stepName === 'addressBookEntries') {
            stepData = await handleAddressBookEntries();
          } else if (stepName === 'outdialAniEntries') {
            stepData = await handleOutdialAniEntries();
          } else if (step.apiCall) {
            stepData = await fetchData(step.apiCall, step.name, null);
          } else {
            addStepProgress(stepName, `Skipping ${step.name} - API not available`, "warning");
            stepData = [];
          }

          const endTime = Date.now();
          const duration = endTime - startTime;

          // Update step completion status
          dataCollectionSteps[stepName].completed = true;
          dataCollectionSteps[stepName].data = stepData;

          setCollectedData(prev => ({
            ...prev,
            [stepName]: {
              ...prev[stepName],
              completed: true,
              data: stepData,
              startTime,
              endTime,
              duration,
              count: Array.isArray(stepData) ? stepData.length : 0
            }
          }));

          setCompletedSteps(prev => [...prev, stepName]);

          addStepProgress(stepName,
            `Completed ${step.name}: ${Array.isArray(stepData) ? stepData.length : 0} items in ${duration}ms`,
            "success",
            { duration, count: Array.isArray(stepData) ? stepData.length : 0 }
          );

        } catch (error) {
          const isOptional = step.optional || !step.required;
          const errorType = isOptional ? "warning" : "error";
          const errorMessage = isOptional
            ? `${step.name} failed but is optional - skipping: ${error.message}`
            : `Failed ${step.name}: ${error.message}`;

          addStepProgress(stepName, errorMessage, errorType);

          // For optional steps, mark as completed with empty data to continue processing
          if (isOptional) {
            dataCollectionSteps[stepName].completed = true;
            dataCollectionSteps[stepName].data = [];

            setCollectedData(prev => ({
              ...prev,
              [stepName]: {
                ...prev[stepName],
                completed: true,
                data: [],
                startTime,
                endTime: Date.now(),
                duration: Date.now() - startTime,
                count: 0
              }
            }));

            setCompletedSteps(prev => [...prev, stepName]);
          }
        }

        processedSteps.add(stepName);
      }));

        retryCount = 0; // Reset retry count on successful batch processing
      }

      setCurrentStep(null);

      // Store in legacy fetchedData format for compatibility
      const legacyData = {};
      Object.entries(dataCollectionSteps).forEach(([stepName, stepData]) => {
        if (stepData.completed && stepData.data) {
          const dataKey = stepData.dataKey;
          if (dataKey) {
            legacyData[dataKey] = stepData.data;
            updateProgress("info", `Mapped ${stepName} -> ${dataKey}: ${stepData.data.length} items`);
          } else {
            updateProgress("warning", `No dataKey found for ${stepName}`);
          }
        } else {
          updateProgress("warning", `Skipped ${stepName}: completed=${stepData.completed}, hasData=${!!stepData.data}`);
        }
      });

      updateProgress("info", `FetchedData keys: ${Object.keys(legacyData).join(', ')}`);
      updateProgress("info", `FetchedData total items: ${Object.keys(legacyData).length}`);

      // Resolve cross-references
      resolveAllCrossReferences();

      updateProgress("success", "All data collection completed. Ready to generate Excel.");

      // Run validation checks after data is fetched
      runValidationChecks();

      // Debug download button state
      const selectedSheets = sheetConfigurations.filter(s => s.selected).length;
      updateProgress("info", `Download button state check: loading=${loading}, fetchedDataKeys=${Object.keys(legacyData).length}, selectedSheets=${selectedSheets}`);
      if (Object.keys(legacyData).length === 0) {
        updateProgress("warning", "Download button disabled: No fetched data available");
      } else if (selectedSheets === 0) {
        updateProgress("warning", "Download button disabled: No sheets selected");
      } else {
        updateProgress("success", "Download button should be enabled now");
      }

      // Set fetched data LAST to ensure all other operations complete first
      setFetchedData(legacyData);

      // Add a small delay to ensure state propagation
      setTimeout(() => {
        updateProgress("info", "Final state check - ensuring download button is enabled");
      }, 100);

    } catch (error) {
      updateProgress("error", `Error during As-Built generation: ${error.message}`);
      console.error("AsBuiltBuilder Error:", error);
    } finally {
      // Always ensure loading is set to false, regardless of success or failure
      setLoading(false);
      updateProgress("info", "Loading state set to false - download button should now be enabled");
    }
  };

  // Special handler for address book entries
  const handleAddressBookEntries = async () => {
    const addressBooksStep = dataCollectionSteps.addressBooks;
    if (!addressBooksStep || !addressBooksStep.completed || !addressBooksStep.data) {
      addStepProgress("addressBookEntries", "Address Books not available for entries collection", "warning");
      return [];
    }

    let allAddressBookEntries = [];
    const addressBooks = addressBooksStep.data;

    if (addressBooks && addressBooks.length > 0) {
      addStepProgress("addressBookEntries", `Fetching entries for ${addressBooks.length} Address Books...`, "info");

      for (const ab of addressBooks) {
        try {
          const entries = await getAddressBookEntries(ab.id, org_id, access_token,
            (type, message) => addStepProgress("addressBookEntries", message, type));

          const entriesWithBookName = entries.map(entry => ({
            ...entry,
            addressBookName: ab.name
          }));

          allAddressBookEntries = allAddressBookEntries.concat(entriesWithBookName);

          addStepProgress("addressBookEntries",
            `Collected ${entries.length} entries from "${ab.name}"`, "info");
        } catch (error) {
          addStepProgress("addressBookEntries",
            `Failed to fetch entries for "${ab.name}": ${error.message}`, "error");
        }
      }
    }

    return allAddressBookEntries;
  };

  // Special handler for outdial ANI entries
  const handleOutdialAniEntries = async () => {
    const outdialAnisStep = dataCollectionSteps.outdialAnis;
    if (!outdialAnisStep || !outdialAnisStep.completed || !outdialAnisStep.data) {
      addStepProgress("outdialAniEntries", "OutDial ANIs not available for entries collection", "warning");
      return [];
    }

    let allOutdialAniEntries = [];
    const outdialAnis = outdialAnisStep.data;

    if (outdialAnis && outdialAnis.length > 0) {
      addStepProgress("outdialAniEntries", `Fetching entries for ${outdialAnis.length} OutDial ANIs...`, "info");

      for (const ani of outdialAnis) {
        try {
          const entries = await getOutdialAniEntries(ani.id, org_id, access_token,
            (type, message) => addStepProgress("outdialAniEntries", message, type));

          const entriesWithAniName = entries.map(entry => ({
            ...entry,
            outdialAniName: ani.name
          }));

          allOutdialAniEntries = allOutdialAniEntries.concat(entriesWithAniName);

          addStepProgress("outdialAniEntries",
            `Collected ${entries.length} entries from "${ani.name}"`, "info");
        } catch (error) {
          addStepProgress("outdialAniEntries",
            `Failed to fetch entries for "${ani.name}": ${error.message}`, "error");
        }
      }
    }

    return allOutdialAniEntries;
  };

  const handleExportAsBuiltExcel = async () => {
    if (Object.keys(fetchedData).length === 0) {
      updateProgress("warning", "No data fetched yet. Please click 'Generate As-Built Data' first.");
      return;
    }

    const selectedSheetsToExport = sheetConfigurations.filter(config => config.selected);
    if (selectedSheetsToExport.length === 0) {
      updateProgress("warning", "No sheets selected for export. Please select at least one sheet.");
      return;
    }

    updateProgress("info", "Generating As-Built Excel workbook from template...");
    setExportProgress(0); // Reset progress at the start of export

    try {
      const templatePath = process.env.PUBLIC_URL + '/wxcc_files/wxcc_mock_AsBuilt.xlsx';
      updateProgress("info", `Loading Excel template from: ${templatePath}`);

      const templateResponse = await fetch(templatePath);
      
      if (!templateResponse.ok) {
        throw new Error(`Failed to load template: ${templateResponse.statusText}`);
      }
      const templateArrayBuffer = await templateResponse.arrayBuffer();
      
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(templateArrayBuffer); // Load the template workbook with all its formatting

      let processedSheetsCount = 0;

      // Define sheets that should never be modified or removed (cover page, etc.)
      const protectedSheets = ["Cover", "Cover Page", "Title", "Title Page"];

      // Also protect any sheet that isn't in our configuration (like the first sheet which is often the cover)
      const configuredSheetNames = sheetConfigurations.map(config => config.name);
      workbook.eachSheet((worksheet) => {
        if (!configuredSheetNames.includes(worksheet.name)) {
          protectedSheets.push(worksheet.name);
          updateProgress("info", `Protecting unconfigured sheet: "${worksheet.name}" from modifications.`);
        }
      });

      // Process only selected sheets in the defined order
      for (const sheetConfig of sheetConfigurations) {
        if (!sheetConfig.selected) {
          // If the sheet exists in the workbook but is not selected, delete it.
          const existingWorksheet = workbook.getWorksheet(sheetConfig.name);
          if (existingWorksheet && !protectedSheets.includes(sheetConfig.name)) {
            workbook.removeWorksheet(existingWorksheet.id);
            updateProgress("info", `Removed unselected sheet: "${sheetConfig.name}" from workbook.`);
          }
          continue; // Skip processing unselected sheets
        }
        
        const sheetName = sheetConfig.name;

        // Skip processing if this is a protected sheet
        if (protectedSheets.includes(sheetName)) {
          updateProgress("info", `Skipping protected sheet: "${sheetName}"`);
          processedSheetsCount++;
          setExportProgress(Math.round((processedSheetsCount / selectedSheetsToExport.length) * 100));
          continue;
        }

        let worksheet = workbook.getWorksheet(sheetName); // Get the worksheet from the loaded template

        if (!worksheet) {
          updateProgress("info", `Sheet "${sheetName}" not found in template. Creating a new sheet.`);
          worksheet = workbook.addWorksheet(sheetName); // Create new sheet
          // Add headers if this is a new sheet and headers are defined in config
          if (sheetConfig.headers && sheetConfig.headers.length > 0) {
            worksheet.addRow(sheetConfig.headers);
            // Apply some basic styling to header if needed
            worksheet.getRow(1).font = { bold: true };
          }
          // For a new sheet, data will start immediately after headers (if any)
          // The spliceRows logic below will correctly handle this as headerStartRow will be 0
          sheetConfig.headerStartRow = 0; // Ensure headerStartRow is 0 for newly created sheets
        }


        // If it's a dynamic sheet with fetched data
        if (sheetConfig.dataKey && fetchedData[sheetConfig.dataKey] !== undefined) { // Check for undefined, not just length
          updateProgress("info", `Populating dynamic data for sheet: "${sheetName}"`);

          let sheetData;
          if (sheetName === "Summary" || sheetName === "Unreferenced Objects") { // Handle new sheets
              sheetData = await mapDataToExcelSheet(sheetName, null); // These sheets use fetchedData directly
          } else {
              sheetData = await mapDataToExcelSheet(sheetName, fetchedData[sheetConfig.dataKey]);
          }

          const { headers, rows } = sheetData;

          // Handle dynamic column generation with styling preservation
          if (headers && headers.length > 0) {
            updateProgress("info", `Applying dynamic columns to sheet: "${sheetName}" (${headers.length} columns, ${rows.length} rows)`);

            // Clear existing data but preserve template structure
            const headerRow = sheetConfig.headerStartRow + 1; // Convert to 1-indexed for ExcelJS

            // Get the existing header row for style reference
            const existingHeaderRow = worksheet.getRow(headerRow);
            const headerStyle = {
              font: existingHeaderRow.font || { bold: true },
              fill: existingHeaderRow.fill || { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } },
              border: existingHeaderRow.border || {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
              },
              alignment: existingHeaderRow.alignment || { horizontal: 'center', vertical: 'middle' }
            };

            // Clear all rows from header onwards
            const startRowToClear = headerRow;
            const numberOfRowsToClear = worksheet.rowCount - headerRow + 1;

            if (numberOfRowsToClear > 0) {
              worksheet.spliceRows(startRowToClear, numberOfRowsToClear);
            }

            // Add new header row with dynamic columns
            const newHeaderRow = worksheet.addRow(headers);

            // Apply styling to header row
            newHeaderRow.eachCell((cell, colNumber) => {
              cell.font = headerStyle.font;
              cell.fill = headerStyle.fill;
              cell.border = headerStyle.border;
              cell.alignment = headerStyle.alignment;

              // Auto-size columns based on header content
              const column = worksheet.getColumn(colNumber);
              const headerLength = headers[colNumber - 1] ? headers[colNumber - 1].length : 10;
              column.width = Math.max(headerLength + 2, 12); // Minimum width of 12
            });

            // Add data rows starting immediately after headers (first row after headers)
            if (rows && rows.length > 0) {
              rows.forEach(row => {
                const dataRow = worksheet.addRow(row);

                // Apply basic styling to data rows
                dataRow.eachCell((cell) => {
                  cell.border = {
                    top: { style: 'thin' },
                    left: { style: 'thin' },
                    bottom: { style: 'thin' },
                    right: { style: 'thin' }
                  };
                  cell.alignment = { vertical: 'top', wrapText: true };
                });
              });
            }

            // Apply alternating row colors for better readability
            for (let i = headerRow + 1; i <= worksheet.rowCount; i++) {
              const row = worksheet.getRow(i);
              if ((i - headerRow) % 2 === 0) {
                row.eachCell((cell) => {
                  cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'FFF8F8F8' }
                  };
                });
              }
            }

            updateProgress("success", `Applied dynamic styling to ${headers.length} columns and ${rows.length} rows in "${sheetName}"`);
          } else {
            updateProgress("warning", `No headers generated for sheet: "${sheetName}"`);
          }
        } else {
          // It's a static sheet or a dynamic sheet with no fetched data, it's already in the workbook
          updateProgress("info", `Keeping static content for sheet: "${sheetName}"`);
        }
        processedSheetsCount++;
        setExportProgress(Math.round((processedSheetsCount / selectedSheetsToExport.length) * 100));
      }

      // Write the modified workbook to a buffer
      const buffer = await workbook.xlsx.writeBuffer();
      const fileName = `Wxcc_As_Built_${org_id || 'no_auth'}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      saveAs(new Blob([buffer], { type: "application/octet-stream" }), fileName);
      updateProgress("success", `As-Built Excel file "${fileName}" generated and downloaded.`);
      setExportProgress(100); // Ensure progress is 100% on completion

    } catch (error) {
      updateProgress("error", `Error during Excel export: ${error.message || String(error)}`);
      console.error("Excel export error:", error);
      setExportProgress(0); // Reset progress on error
    }
  };

  const handleSheetSelectionChange = (sheetName) => {
    setSheetConfigurations(prevConfigs =>
      prevConfigs.map(config =>
        config.name === sheetName ? { ...config, selected: !config.selected } : config
      )
    );
  };


  return (
    <div style={{ padding: '20px', maxWidth: '900px', margin: 'auto', fontFamily: 'Inter, sans-serif', backgroundColor: '#f9f9f9', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
      <h1 style={{ color: '#333', textAlign: 'center', marginBottom: '25px' }}>Webex CX As-Built Builder</h1>
      <p style={{ textAlign: 'center', color: '#555', marginBottom: '30px' }}>
        This tool fetches your current Webex Contact Center configuration and generates an "As-Built" Excel workbook.
        It uses your provided authentication to retrieve live data and populates a pre-defined template.
      </p>

      {!authData || !authData.org_id || !authData.access_token ? (
        <div style={{ padding: '15px', backgroundColor: '#fff3cd', border: '1px solid #ffeeba', borderRadius: '6px', marginBottom: '20px', textAlign: 'center', color: '#856404' }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>Authentication Required:</p>
          <p style={{ margin: '5px 0 0 0', fontSize: '0.9em' }}>
            Please go back to the main menu and authenticate via "DDW Import" or "Tools" to enable data fetching.
            Without authentication, only a blank template will be generated.
          </p>
        </div>
      ) : (
        <p style={{ textAlign: 'center', color: '#666', fontSize: '0.9em', marginBottom: '20px' }}>
          Authenticated with Org ID: <span style={{ fontWeight: 'bold' }}>{authData.org_id}</span>
        </p>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginBottom: '30px' }}>
        <button
          onClick={handleGenerateAsBuilt}
          disabled={loading || !authData || !authData.org_id || !authData.access_token}
          style={{
            padding: '12px 25px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: (loading || !authData || !authData.org_id || !authData.access_token) ? 'not-allowed' : 'pointer',
            opacity: (loading || !authData || !authData.org_id || !authData.access_token) ? 0.7 : 1,
            fontSize: '1em',
            fontWeight: '600',
            transition: 'background-color 0.3s ease, opacity 0.3s ease',
            boxShadow: '0 2px 8px rgba(0, 123, 255, 0.2)'
          }}
        >
          {loading ? "Generating Data..." : "Generate As-Built Data"}
        </button>

        <button
          onClick={handleExportAsBuiltExcel}
          disabled={!canDownload}
          style={{
            padding: '12px 25px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: canDownload ? 'pointer' : 'not-allowed',
            opacity: canDownload ? 1 : 0.7,
            fontSize: '1em',
            fontWeight: '600',
            transition: 'background-color 0.3s ease, opacity 0.3s ease',
            boxShadow: '0 2px 8px rgba(40, 167, 69, 0.2)'
          }}
        >
          Export As-Built Excel
        </button>
      </div>

      {exportProgress > 0 && exportProgress < 100 && (
        <div style={{ width: '100%', backgroundColor: '#e0e0e0', borderRadius: '4px', marginTop: '15px', marginBottom: '15px' }}>
          <div
            style={{
              width: `${exportProgress}%`,
              backgroundColor: '#007bff',
              height: '20px',
              borderRadius: '4px',
              textAlign: 'center',
              color: 'white',
              lineHeight: '20px',
              fontSize: '0.8em',
              transition: 'width 0.3s ease-in-out'
            }}
          >
            {exportProgress}%
          </div>
        </div>
      )}

      <div style={{ borderTop: "1px solid #eee", paddingTop: "20px", marginTop: "20px" }}>
        <h2 style={{ color: "#333", marginBottom: '15px' }}>Select Sheets for Export:</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', marginBottom: '20px', maxHeight: '250px', overflowY: 'auto', paddingRight: '10px' }}>
          {sheetConfigurations.map((config) => (
            <label key={config.name} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.9em' }}>
              <input
                type="checkbox"
                checked={config.selected}
                onChange={() => handleSheetSelectionChange(config.name)}
                style={{ marginRight: '8px' }}
              />
              {config.name}
            </label>
          ))}
        </div>
      </div>


      {/* Enhanced Progress Display */}
      {Object.keys(collectedData).length > 0 && (
        <div style={{ borderTop: "1px solid #eee", paddingTop: "20px", marginTop: "20px" }}>
          <h2 style={{ color: "#333", marginBottom: '15px' }}>Collection Progress:</h2>

          {/* Overall Progress Bar */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
              <span style={{ fontSize: '0.9em', color: '#666' }}>
                Overall Progress: {completedSteps.length} / {totalSteps} steps
              </span>
              <span style={{ fontSize: '0.9em', color: '#666' }}>
                {totalSteps > 0 ? Math.round((completedSteps.length / totalSteps) * 100) : 0}%
              </span>
            </div>
            <div style={{ width: '100%', backgroundColor: '#e0e0e0', borderRadius: '4px', height: '8px' }}>
              <div
                style={{
                  width: `${totalSteps > 0 ? (completedSteps.length / totalSteps) * 100 : 0}%`,
                  backgroundColor: '#28a745',
                  height: '8px',
                  borderRadius: '4px',
                  transition: 'width 0.3s ease-in-out'
                }}
              />
            </div>
          </div>

          {/* Step Status Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '10px',
            marginBottom: '20px'
          }}>
            {Object.entries(collectedData).map(([stepName, stepData]) => (
              <div key={stepName} style={{
                padding: '8px',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: '0.8em',
                backgroundColor: stepData.completed ? '#d4edda' : currentStep === stepName ? '#fff3cd' : '#f8f9fa'
              }}>
                <div style={{ fontWeight: 'bold', color: '#333' }}>{stepData.name}</div>
                <div style={{ color: '#666', fontSize: '0.9em' }}>
                  {stepData.completed ? '✓ Completed' : currentStep === stepName ? '⏳ Processing...' : '⏸ Waiting'}
                  {stepData.count > 0 && ` (${stepData.count} items)`}
                </div>
                {stepData.duration > 0 && (
                  <div style={{ color: '#666', fontSize: '0.8em' }}>
                    {(stepData.duration / 1000).toFixed(1)}s
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ borderTop: "1px solid #eee", paddingTop: "20px", marginTop: "20px" }}>
        <h2 style={{ color: "#333", marginBottom: '15px' }}>Progress Log:</h2>
        <div
          ref={logContainerRef}
          style={{
            backgroundColor: '#e9ecef',
            padding: '15px',
            borderRadius: '6px',
            maxHeight: '300px',
            overflowY: 'auto',
            border: '1px solid #dee2e6',
            marginBottom: '20px'
          }}
        >
          <ul style={{ listStyleType: "none", padding: 0, margin: 0 }}>
            {progress.map((item, index) => (
              <li
                key={index}
                style={{
                  marginBottom: "5px",
                  fontSize: "0.9em",
                  ...getMessageStyle(item.type),
                }}
              >
                <span style={{ fontWeight: "normal", color: "#666" }}>
                  [{item.timestamp}]
                </span>
                {item.stepName && (
                  <span style={{ fontWeight: "bold", color: "#444", marginLeft: "5px" }}>
                    [{item.stepName}]
                  </span>
                )}
                <span style={{ marginLeft: "5px" }}>{item.message}</span>
                {item.memoryUsage && (
                  <span style={{ fontSize: "0.8em", color: "#999", marginLeft: "10px" }}>
                    ({Math.round(item.memoryUsage / 1024 / 1024)}MB)
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div style={{ textAlign: 'center', marginTop: '30px' }}>
        <button
          onClick={onBackToOptionSelector}
          style={{
            padding: '10px 20px',
            backgroundColor: '#6c757d',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer',
            fontSize: '0.9em',
            transition: 'background-color 0.3s ease'
          }}
        >
          Back to Option Selector
        </button>
      </div>
    </div>
  );
};

export default AsBuiltBuilder;
